"use client";

import { useMemo, useState } from "react";
import dayjs from "dayjs";

import { useGetSavingsSummaryQuery } from "@/app/redux/features/api/rewards";
import ActiveMemberBadge from "@/app/(custom-header)/botb-pass-members-home/components/ActiveMemberBadge";
import SavingsCard from "@/app/(general-header)/subscription/components/SavingsCard";
import ChevronsRightIcon from "@/app/shared-components/icons/ChevronsRightIcon";
import ClockIcon from "@/app/shared-components/icons/ClockIcon";
import { useManagementSubscriptionContext } from "../Context";
import PaymentOverdueCancelModal from "./PaymentOverdueCancelModal";

const PAST_DUE_STATUSES: string[] = ["open"];

function formatMemberSince(unixSeconds?: number): string {
  if (!unixSeconds) return "";
  const date = new Date(unixSeconds * 1000);
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export default function MemberHero() {
  const { subscriptionPlan, subsWillBeCancelled, setShouldShowCancelSubscriptionStep, invoicesList } =
    useManagementSubscriptionContext();
  const [showOverdueCancelModal, setShowOverdueCancelModal] = useState(false);

  const { data: savings } = useGetSavingsSummaryQuery();

  const pastDueInvoice = useMemo(
    () => invoicesList.find(invoice => PAST_DUE_STATUSES.includes(invoice.status)),
    [invoicesList]
  );

  const remainingTime = useMemo(() => {
    const diff = dayjs(subscriptionPlan.current_period_end * 1000).diff(dayjs(), "milliseconds");
    if (diff <= 0) return "0 hours";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return days === 1 ? `${days} day` : `${days} days`;
    return hours === 1 ? `${hours} hour` : `${hours} hours`;
  }, [subscriptionPlan]);

  const percentageForRemaining = useMemo(() => {
    const total = subscriptionPlan.current_period_end - subscriptionPlan.current_period_start;
    const remaining = subscriptionPlan.current_period_end - dayjs().unix();
    return Math.min(Math.abs(100 - (remaining / total) * 100), 100);
  }, [subscriptionPlan]);

  const periodEndDate = useMemo(
    () => dayjs(subscriptionPlan.current_period_end * 1000).format("DD MMMM YYYY"),
    [subscriptionPlan]
  );

  const memberSince = useMemo(() => formatMemberSince(savings?.MemberSince), [savings?.MemberSince]);
  const displayName = savings?.FirstName && savings?.Surname ? `${savings.FirstName} ${savings.Surname}` : "";
  const planLabel = subscriptionPlan?.name ? `${subscriptionPlan.name} Plan` : "";
  const currentPeriod =
    savings?.CurrentPeriodStart && savings?.CurrentPeriodEnd
      ? `${savings.CurrentPeriodStart} - ${savings.CurrentPeriodEnd}`
      : "";

  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)] lg:items-start gap-y-6 lg:gap-y-8 lg:gap-x-10 px-7 lg:px-8 pt-8 lg:pt-16 pb-6">
        {/* Member info — centered on mobile, left-aligned on desktop */}
        <div className="order-1 lg:col-start-1 lg:row-start-1 flex flex-col gap-1.5 lg:gap-3 items-center lg:items-start w-full">
          <div className="flex flex-col gap-1 lg:gap-3 items-center lg:items-start w-full">
            {subsWillBeCancelled ? (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-4 border border-red-3">
                <ClockIcon className="size-3.5 shrink-0 text-red-2" />
                <span className="text-red-2 text-xs lg:text-sm font-medium font-jost uppercase leading-none">
                  Cancelled
                </span>
              </div>
            ) : (
              <ActiveMemberBadge />
            )}

            <div className="normal-case text-center lg:text-left !mb-0 text-dark-1 text-[22px] lg:text-[32px] font-bold font-jost leading-7 lg:leading-10">
              {displayName}
            </div>
          </div>

          <div className="flex items-center justify-center lg:justify-start gap-1 lg:gap-2 flex-wrap">
            {planLabel && (
              <span className="text-gray-1 lg:text-gray-5 text-[11px] lg:text-base font-medium font-jost leading-3.5 lg:leading-5.5">
                {planLabel}
              </span>
            )}
            {planLabel && memberSince && (
              <div className="size-[5px] lg:size-1.5 rounded-full bg-gray-1 lg:bg-gray-5 shrink-0" />
            )}
            {memberSince && (
              <span className="text-gray-1 lg:text-gray-5 text-[11px] lg:text-base font-medium font-jost leading-3.5 lg:leading-5.5">
                Member since {memberSince}
              </span>
            )}
          </div>
        </div>

        {/* Savings card — sits between member info and actions on mobile, right column on desktop */}
        <div className="order-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2 relative w-full lg:max-w-[435px] lg:justify-self-end">
          <img
            src="/assets/images/botb-pass-members/coins.png"
            alt=""
            className="absolute -left-12 lg:-left-[70px] top-1/2 lg:top-6 -translate-y-1/2 lg:translate-y-0 size-[88px] lg:size-[140px] pointer-events-none select-none z-0 object-cover"
          />
          <div className="relative z-10">
            <SavingsCard
              totalSavings={savings?.LifetimeTotal ?? 0}
              currentSavings={savings?.CurrentPeriodTotal ?? 0}
              currentPeriod={currentPeriod}
              showCoin="desktop"
            />
          </div>
        </div>

        {/* Progress (cancelled) + action */}
        <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2 flex flex-col gap-2 lg:gap-4 items-start w-full">
          {subsWillBeCancelled && (
            <div className="flex flex-col gap-2 w-full">
              {percentageForRemaining < 100 && (
                <div className="text-secondary font-jost text-xs lg:text-base font-medium leading-4 lg:leading-5.5">
                  {remainingTime} left
                </div>
              )}
              <div className="relative bg-gray-7 h-1.5 lg:h-2 rounded-full w-full overflow-hidden">
                <div
                  className={`absolute left-0 inset-y-0 h-full rounded-full ${percentageForRemaining >= 50 ? "bg-progress-subscription" : "bg-green-6"}`}
                  style={{ width: `${percentageForRemaining}%` }}
                />
              </div>
              <div className="text-gray-5 font-jost text-[10px] lg:text-[13px] font-normal leading-3 lg:leading-4.5">
                Your subscription will be cancelled on <span className="text-secondary">{periodEndDate}</span>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 lg:gap-4 items-start w-full">
            <button
              onClick={() => {
                if (subsWillBeCancelled) {
                  setShouldShowCancelSubscriptionStep(true);
                  return;
                }
                if (pastDueInvoice) {
                  setShowOverdueCancelModal(true);
                  return;
                }
                setShouldShowCancelSubscriptionStep(true);
              }}
              title={subsWillBeCancelled ? "Don't cancel your subscription" : "Cancel Subscription"}
              className={`theme-btn theme-btn-xs lg:theme-btn-md uppercase min-w-[173px] lg:min-w-[240px] ${
                subsWillBeCancelled ? "theme-btn-primary" : "theme-btn-primary-outline text-primary"
              }`}
            >
              {subsWillBeCancelled ? "Don't cancel your subscription" : "Cancel Subscription"}
              <ChevronsRightIcon
                variant={2}
                className={`size-4 lg:size-5 shrink-0 ${subsWillBeCancelled ? "text-white" : "text-primary"}`}
              />
            </button>

            {!subsWillBeCancelled && (
              <div className="text-gray-5 font-jost text-[10px] lg:text-[13px] font-normal leading-3 lg:leading-4.5">
                Your subscription renews on <span className="text-secondary">{periodEndDate}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {showOverdueCancelModal && pastDueInvoice && (
        <PaymentOverdueCancelModal
          invoiceId={pastDueInvoice.id}
          invoiceAmountDue={pastDueInvoice.amount_due}
          onClose={() => setShowOverdueCancelModal(false)}
        />
      )}
    </section>
  );
}
